import { IControlQualityCheck } from 'common/api/type-def';
import { ViewType } from 'common/constants';
import {
	setAIModelDetail,
	setAIRecipeDetail,
	setControlQuality,
} from 'common/store/reducer/simulationReducer';

import {
	useState,
	useEffect,
	useDispatch,
	useSelector,
	Grid,
	Stack,
	Typography,
	Divider,
	SettingsIcon,
	TabBar,
	setRegulationTableSearchText,
	setAppliedfilterKeys,
	scrollToTop,
	toSentenceCase,
	setSimulationTabIndex,
	setModelTabIndex,
	AI_TYPE,
	AI_SIMULATION_TITLE,
	aiModelTabs,
	modelSecTools,
	simulationSecTools,
	useFeatureFlag,
	FeatureFlagsKeysEnum,
	Banner,
	AI_SIMULATION_DASHBOARD,
	SectionToolbar,
	AICard,
	styles,
	RootState,
	setRegulationText,
	setSelectedRecipe,
	aiRecipes,
	nestedAIModels,
	SimulationRecipes,
	SimulationModels,
	SharedSimulations,
	IAIRecipeDetails,
	IAIModelDetails,
	AlertComponent,
	useCallback,
	setToastMessage,
	Button,
	useLocation,
} from './SimulationArenaModules';

const SimulationArena = () => {
	const dispatch = useDispatch();

	const simulationViewType = useSelector(
		(state: RootState) => state.simulationArena.simulationViewTypeValue,
	);

	const location = useLocation();

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const { RECIPE, MODEL } = AI_TYPE;
	const { SIMULATION_ARENA, MY_SIMULATIONS, AI_RECIPES, MODELS } =
		AI_SIMULATION_TITLE;

	const modelTabIndex = useSelector(
		(state: RootState) => state?.simulationArena?.modelTabIndex,
	);
	const simulationTabIndex = useSelector(
		(state: RootState) => state?.simulationArena?.simulationTabIndex,
	);
	const [viewType, setViewType] = useState<ViewType>(simulationViewType);

	useEffect(() => {
		dispatch(setSelectedRecipe(''));
		dispatch(setRegulationTableSearchText(''));
		dispatch(setAppliedfilterKeys([]));
		dispatch(setRegulationText(''));
		dispatch(setControlQuality({} as IControlQualityCheck));
		dispatch(setAIRecipeDetail({} as IAIRecipeDetails));
		dispatch(setAIModelDetail({} as IAIModelDetails));
	}, []);

	useEffect(() => {
		if (location.state?.shouldShowToast) {
			dispatch(
				setToastMessage({
					message: location.state?.message,
					severity: 'success',
					isToastOpen: true,
				}),
			);
		}
	}, [location]);

	useEffect(() => {
		setViewType(simulationViewType);
	}, [simulationViewType, simulationTabIndex]);

	const onTabSelect = (index: number) => {
		setModelTabIndex(index);
		dispatch(setModelTabIndex(index));
	};

	useEffect(() => {
		scrollToTop();
	}, []);

	const displayModels = (modelType: string) => (
		<Grid
			className={`${styles['section-content']} ${styles['display-models']}`}
			container
			spacing={3}
		>
			{nestedAIModels[modelType].map((res: any) => (
				<Grid
					item
					key={res.id}
					xs={12}
					sm={12}
					md={4}
					lg={4}
					xl={4}
					direction="row"
				>
					<AICard
						title={res.label}
						data={{}}
						type={MODEL}
						key={res.id}
						path={res.path}
						icon={res.icon}
					>
						<Typography className={styles['card-description']}>
							{toSentenceCase(res.desc)}
						</Typography>
					</AICard>
				</Grid>
			))}
		</Grid>
	);

	const isAILandingPageSettingEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_AI_LANDING_PAGE_SETTING_BUTTON_ENABLED,
	);
	const isAILandingPageSectionQATabEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_AI_LANDING_PAGE_SECTION_QA_TAB_ENABLED,
	);

	const simulationTabs = [
		{ name: 'Recipes' },
		{ name: 'Models' },
		{ name: 'Shared' },
	];

	const tabs = [
		{
			id: 1,
			isEnabled: true,
			children: displayModels('create'),
		},
		{
			id: 2,
			isEnabled: true,
			children: displayModels('improve'),
		},
		{
			id: 3,
			isEnabled: true,
			children: displayModels('summarize'),
		},
		{
			id: 4,
			isEnabled: isAILandingPageSectionQATabEnabled,
			children: displayModels('q_A'),
		},
	];

	const isEnabledTabValues = tabs
		.filter((tab) => tab.isEnabled)
		.map((tab) => tab);

	const simulationTabPanels = [
		{
			id: 1,
			children: <SimulationRecipes />,
		},
		{
			id: 2,
			children: <SimulationModels />,
		},
		{
			id: 3,
			children: <SharedSimulations />,
		},
	];

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	const handleSimulationTabChange = (newTab: number) => {
		dispatch(setSimulationTabIndex(newTab));
	};

	return (
		<Grid xs={12} className={`${styles['simulation-arena']}`}>
			<Banner>
				<Stack p={2} className={styles['simulation-header-wrapper']}>
					<Typography className={styles['simulation-banner-heading']}>
						{SIMULATION_ARENA}
					</Typography>
					<Typography mt={0} className={styles['simulation-banner-content']}>
						{AI_SIMULATION_DASHBOARD.AI_SIMULATION_BANNER}
					</Typography>
				</Stack>
			</Banner>

			{isAILandingPageSettingEnabled && (
				<Stack direction="row" justifyContent="flex-end">
					<Button
						compClass={styles['setting-btn']}
						btnType="secondary"
						variant="outlined"
					>
						<SettingsIcon />
						<Typography fontWeight={500}>Settings</Typography>
					</Button>
				</Stack>
			)}
			<Stack className={styles['section-container']}>
				<Typography
					className={`${styles['section-title']} ${styles['title-ai-recipes']}`}
				>
					{AI_RECIPES}
				</Typography>
				<SectionToolbar types={modelSecTools} />
				<Grid className={styles['section-content']} container spacing={3}>
					{aiRecipes.map((res) => (
						<Grid
							item
							key={res.id}
							xs={12}
							sm={12}
							md={4}
							lg={4}
							xl={4}
							direction="row"
						>
							<AICard
								title={res.label}
								data={{}}
								type={RECIPE}
								key={res.id}
								path={res.path}
								icon={res.icon}
							>
								<Typography className={styles['card-description']}>
									{res.desc}
								</Typography>
							</AICard>
						</Grid>
					))}
				</Grid>
			</Stack>

			<Divider sx={{ my: 4 }} />

			<Stack className={styles['section-container']}>
				<Typography
					className={`${styles['section-title']} ${styles['model-title']}`}
				>
					{MODELS}
				</Typography>

				<TabBar
					activeTab={modelTabIndex}
					details={aiModelTabs}
					showCount={false}
					components={isEnabledTabValues}
					showButton={false}
					onTabChange={onTabSelect}
					childComponent={<SectionToolbar types={modelSecTools} />}
				/>
			</Stack>

			<Divider sx={{ my: 4 }} />

			<Stack
				className={styles['section-container']}
				data-testId="simulation-section"
			>
				<Typography
					className={`${styles['section-title']} ${styles['model-title']}`}
				>
					{MY_SIMULATIONS}
				</Typography>
				<TabBar
					testId="simulation-tab-testid"
					activeTab={simulationTabIndex}
					details={simulationTabs}
					showCount={false}
					components={simulationTabPanels}
					showButton={false}
					onTabChange={handleSimulationTabChange}
					childComponent={
						<SectionToolbar
							viewType={viewType}
							onToggleView={setViewType}
							types={simulationSecTools}
						/>
					}
				/>
			</Stack>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
				data-testid="alert-component"
			/>
		</Grid>
	);
};
export default SimulationArena;
